// @flow
import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { createUseStyles } from 'react-jss';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

type Props = {
  name: string,
  description: string,
  imageUrl: string,
  outputType: string,
  aspectRatio: string,
}

const mimeTypes = {
  avif: 'image/avif',
  jpg: 'image/jpeg',
  png: 'image/png',
}

const ImageCropper = forwardRef((props: Props, ref: Reference) => {
  const { name, description, imageUrl, aspectRatio, outputType: initialOutputType } = props;
  const c = useStyles();
  const imageFileName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
  const imageFileNameWithoutExtension = imageFileName.substring(0, imageFileName.lastIndexOf('.'))
  const outputType = initialOutputType?.toLowerCase() || 'jpg';
  const mimeType = mimeTypes[outputType] || mimeTypes.jpg;

  const cropperRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getCroppedFile: (callback) => {
      if (cropperRef.current) {
        const canvas = cropperRef.current.cropper.getCroppedCanvas();
        canvas.toBlob(b => {
          b.name = `${imageFileNameWithoutExtension}-${name || Date.now()}.${outputType}`;
          b.lastModifiedDate = new Date();

          callback({ name, file: b })
        }, mimeType)
      }
    }
  }));

  return (
    <div className={c.container}>
      <div>
        {description}
        <br />
        {`Aspect Ratio: ${aspectRatio || 'free'}`}
      </div>
      <Cropper
        style={{ width: '90%' }}
        zoomTo={0.5}
        aspectRatio={parseAspectRatio(aspectRatio)}
        preview=".img-preview"
        src={imageUrl}
        viewMode={1}
        minCropBoxHeight={10}
        minCropBoxWidth={10}
        background={false}
        responsive={true}
        autoCropArea={1}
        ref={cropperRef}
        guides={true}
      />
    </div>
  );
});
ImageCropper.displayName = 'ImageCropper';

const useStyles = createUseStyles({
  container: {
    marginBottom: '30px',
    marginTop: '20px',
  },
});

function parseAspectRatio(value) {
  if (!value) return;

  const aspectRatioInfo = value.split('/');
  const aspectRatio = parseInt(aspectRatioInfo[0]) / parseInt(aspectRatioInfo[1]);

  return aspectRatio;
}

export default ImageCropper;
